import { DateTime } from "luxon";
import { sessionStorageService } from "../services/SessionStorageService";
import { trackErrorEvent } from "./analytics";

// Within this long of a recovery reload, another failed chunk is a real outage, not a stale tab.
const RELOAD_GUARD_MS = 5 * 60 * 1000;

// A reload unloads the page well within this; one still here afterwards was cancelled (a `beforeunload`
// prompt for unsaved work), and the page that failed to load has to say so rather than spin forever.
export const RELOAD_WAIT_MS = 5000;

/**
 * The module a dynamic import resolved to. When the reload below is already under way, Vite resolves the
 * failed import with `undefined`; reading an export from that would report an error for a page about to be
 * replaced, so this waits for the reload instead, and fails only if the page is still here `RELOAD_WAIT_MS` later.
 */
export const moduleUnlessReloading = <Module>(module: Module): Promise<Module> =>
	module === undefined
		? new Promise<Module>((_resolve, reject) => {
				window.setTimeout(() => reject(new Error("A file this page needs failed to load, and the page did not reload")), RELOAD_WAIT_MS);
			})
		: Promise.resolve(module);

/**
 * Vite's documented recovery for a lazy chunk that failed to load, usually because a deploy removed
 * the files a tab's old index.html names: reload once to pick up the new build. The guard keeps a
 * chunk that still fails from reloading forever; then the error reaches the ErrorBoundary.
 */
export const installPreloadErrorReload = (): void => {
	window.addEventListener("vite:preloadError", (event) => {
		const now = DateTime.now().toMillis();
		const lastReloadAt = sessionStorageService.getLastChunkReloadAt();
		if (lastReloadAt !== null && now - lastReloadAt < RELOAD_GUARD_MS) return;

		sessionStorageService.setLastChunkReloadAt(now);
		// Storage blocked: the reloaded page couldn't tell it already tried, so it could loop.
		if (sessionStorageService.getLastChunkReloadAt() !== now) return;

		trackErrorEvent("chunk_preload_error_reload", event.payload);
		event.preventDefault();
		window.location.reload();
	});
};
